import {GraphyoActivity,flushGraphyoAnswers} from "./GraphyoActivity";
import {SharedGraphyoActivity,type SharedGraphyoHandlers} from './SharedGraphyoActivity';
import {PedagogicalCorrection} from './PedagogicalCorrection';
import {ChemicalLaboratory} from './ChemicalLaboratory';
import {chemicalLaboratoryId} from './chemical-lab-config';
import {chemicalLabAction,parseChemicalWorld} from './chemical-lab-session';
import {mergeChemicalMeasurements} from './chemical-lab-measurements';
import {TransferJournal} from './TransferJournal';
import type {WorldAction} from './mini-lab-mechanics';
import {ReactionProgressTable,StructuredFields} from './ReactionProgress';
import {progressCorrectionAnswer} from './reaction-progress';
import {measurementEngineId,type ExperimentAction} from './measurement-engine';
import {MeasurementAndTitrationEngine} from './MeasurementAndTitrationEngine';
import {experimentAnswers} from './measurement-engine-answers';
import {QuestionCorrection} from "./QuestionCorrection";
import {activityPdfResource} from './activity-pdf-resource';
import {automaticQuestionVerdict} from "./activity-question-grading";
import {SavedExperimentReports} from "./SavedExperimentReports";
import {ProtocolOrderGame} from './ProtocolOrderGame';
import {ColorimetricTitrationDiagram} from './ColorimetricTitrationDiagram';
import {ChoiceHint} from './ChoiceHint';
import {encodeTitrationLabelingAnswer} from './titration-labeling';
import {allowsAnswerAttachments,allowsAnswerAttachmentsForActivity,savedManipulation} from './activity-response-policy';
import {ValidatedManipulation} from './ValidatedManipulation';
import {gradeActivityQuestion} from './activity-question-grading';
import {StaticGraph} from './StaticGraph';
import {TitrationDiagram} from './TitrationDiagram';
import {patchVinegarPresentation} from './vinaigre-presentation';
import {graphSourcePoints} from './graph-source-data';
import {InteractiveGraph} from './InteractiveGraph';
import {CalibrationTable} from "./CalibrationTable";
import {CalibrationGraph} from "./CalibrationGraph";
import {calibrationRows} from "./calibration-data";
import {measurementMode} from "./animation-choices";
import {useEvaluationFocus} from "./useEvaluationFocus";
import {copyReleased, evidenceLocked} from "./copy-permissions";
import {attemptSessionId} from "./activity-continuity";
import {normalizeActivity, activityPackageForEditing} from "./normalized-activity";
import { MathText } from "./MathText";
import {DocumentMolecules} from './DocumentMolecules';
import {formatChemistryText} from './chemical-typography';
import { LibraryAnswerPhotos } from "./LibraryAnswerPhotos";
import { checkpointEvidenceIds, isEvidenceBlock, questionLabel } from "./interactive-activity-checkpoints";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import {changedAnswers, reconcileAnswers, latestAcknowledgedAttempt} from "./activity-live";
import {studentWorkStatus} from "./student-work-view";
import {trackingDetails} from "./session-tracking";
import type {ReferenceCorrection} from "./doppler-correction-upgrade";
import {withCurrentDopplerRuntimeAssets} from "./doppler-runtime-assets";
import {withCurrentAttenuationRuntimeBlocks} from "./attenuation-runtime-blocks";
import type {
  ActivityVersion,
  ActivityCheckpoint,
  LibraryCommand,
  StudentAttempt,
  HelpUsage,
  CompetenceLevel,
} from "./activity-library";
import { BlockPreview } from "./InteractiveActivityBlocks";
import {ExperimentFrame,type ExperimentEvent} from './ExperimentFrame';
import {resourceForAsset} from './interactive-resources';
import {applyMeasurementEvent,measurementAnswerFromSource,sourceMeasurements} from './resource-measurements';
import {ResourceMeasurementAnswer} from './ResourceMeasurementAnswer';
import "./interactive-activity-studio.css";
import type {
  InteractiveActivityBlock,
  InteractiveMeasurementTableBlock,
} from "./interactive-activity";

const grades: CompetenceLevel[] = ["A", "B", "C", "D", "NE", "ABS"];
const gradeLabel = (grade: string) =>
  grade === "ABS" ? "Absent" : grade === "NE" ? "Non évalué" : grade;

function TeacherRedTextAnnotation({value="", editable, busy=false, onSave}: {
  value?: string; editable: boolean; busy?: boolean; onSave?: (text: string) => Promise<void>;
}) {
  const [draft, setDraft] = useState(value);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  useEffect(() => { if (!dirty) setDraft(value); }, [value, dirty]);
  if (!editable && !value) return null;
  return <aside className="teacher-red-annotation">
    {editable ? <>
      <label><span>Annotation professeur en rouge</span>
        <textarea value={draft} maxLength={5000} placeholder="Ajouter une remarque directement sur cette production…"
          onChange={e=>{setDraft(e.target.value);setDirty(true);setMessage("");}} />
      </label>
      <div>
        <button type="button" disabled={busy||saving||!dirty} onClick={async()=>{
          if(!onSave)return;
          setSaving(true); setMessage("");
          try {await onSave(draft); setDirty(false); setMessage(draft.trim()?"Annotation enregistrée.":"Annotation supprimée.");}
          catch(e){setMessage(e instanceof Error?e.message:"Enregistrement impossible.");}
          finally{setSaving(false);}
        }}>{saving?"Enregistrement…":draft.trim()?"Enregistrer l’annotation":"Supprimer l’annotation"}</button>
        {dirty && <small>Modification non enregistrée</small>}
      </div>
      {message && <small role="status">{message}</small>}
    </> : <p>{value}</p>}
  </aside>;
}

function TeacherCorrectionEditor({baseText="", overrideText, busy=false, onSave}: {
  baseText?: string; overrideText?: string; busy?: boolean; onSave: (text: string) => Promise<void>;
}) {
  const effective = overrideText ?? baseText;
  const [draft,setDraft] = useState(effective);
  const [dirty,setDirty] = useState(false);
  const [saving,setSaving] = useState(false);
  const [message,setMessage] = useState("");
  useEffect(()=>{if(!dirty)setDraft(effective);},[effective,dirty]);
  const customized = overrideText !== undefined;
  async function save(text: string) {
    setSaving(true); setMessage("");
    try {
      await onSave(text);
      setDraft(text.trim() ? text : baseText);
      setDirty(false);
      setMessage(text.trim() ? "Correction personnalisée enregistrée." : "Correction d’origine restaurée.");
    } catch(e) {setMessage(e instanceof Error?e.message:"Enregistrement impossible.");}
    finally {setSaving(false);}
  }
  return <div className="teacher-correction-editor">
    <label><span>Correction visible après restitution</span>
      <textarea value={draft} maxLength={10000} placeholder="Rédiger la correction pour cette copie…"
        onChange={e=>{setDraft(e.target.value);setDirty(true);setMessage("");}} />
    </label>
    <div>
      <button type="button" disabled={busy||saving||!dirty||!draft.trim()} onClick={()=>void save(draft)}>
        {saving?"Enregistrement…":"Enregistrer cette correction"}
      </button>
      {customized && <button type="button" className="secondary" disabled={busy||saving} onClick={()=>void save("")}>Revenir à la correction d’origine</button>}
      {dirty && <small>Modification non enregistrée</small>}
    </div>
    {message && <small role="status">{message}</small>}
  </div>;
}


function MeasurementAnswer({
  block,
  value,
  disabled,
  onChange,
}: {
  block: InteractiveMeasurementTableBlock;
  value: string;
  disabled: boolean;
  onChange: (v: string) => void;
}) {
  let rows: Record<string, string>[];
  try {
    rows = JSON.parse(value);
  } catch {
    rows = [];
  }
  if (!Array.isArray(rows) || !rows.length)
    rows = structuredClone(block.initialRows ?? Array.from({ length: block.minRows ?? 3 }, () => ({})));
  return (
    <div>
      <p><MathText text={block.prompt} /></p>
      <table className="library-measures">
        <thead>
          <tr>
            {block.columns.map((c) => (
              <th key={c.id}>
                {c.label} {c.unit && `(${c.unit})`}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {block.columns.map((c) => (
                <td key={c.id}>
                  <input
                    aria-label={`${c.label} ligne ${i + 1}`}
                    type={c.input === "number" ? "number" : "text"}
                    step={c.input === "number" ? "any" : undefined}
                    disabled={disabled||block.readOnlyColumnIds?.includes(c.id)}
                    value={row[c.id] ?? ""}
                    onChange={(e) =>
                      onChange(
                        JSON.stringify(
                          rows.map((r, j) =>
                            i === j ? { ...r, [c.id]: e.target.value } : r,
                          ),
                        ),
                      )
                    }
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {!disabled && rows.length < (block.maxRows ?? 30) && (
        <button onClick={() => onChange(JSON.stringify([...rows, {}]))}>
          Ajouter une mesure
        </button>
      )}
    </div>
  );
}

function AnswerBlock({
  block,
  value,
  disabled,
  onChange,
  allAnswers, allBlocks, mode, printMode=false, readOnly=false, graphyoReadOnly=disabled, sharedGraphyo,
}: {
  readOnly?:boolean;
  graphyoReadOnly?:boolean;
  sharedGraphyo?:SharedGraphyoHandlers;
  printMode?:boolean;
  allAnswers:Record<string,string>;
  allBlocks:InteractiveActivityBlock[];
  mode:import("./interactive-activity").ExperimentalMode;
  block: InteractiveActivityBlock;
  value: string;
  disabled: boolean;
  onChange: (v: string) => void;
}) {
  if(block.type==='question'&&block.diagramLabeling)return <><p><MathText text={block.prompt}/></p><ColorimetricTitrationDiagram config={block.diagramLabeling} value={value} onChange={onChange} disabled={disabled||readOnly} staticMode={printMode||readOnly}/></>;
  if(block.type==='question'&&(block.progressTable||block.responseFields))return <><p><MathText text={block.prompt}/></p>{block.progressTable?<ReactionProgressTable equationAnswer={allAnswers[block.progressTable.equationSourceBlockId??'']} config={block.progressTable} value={value} onChange={onChange} disabled={disabled||readOnly} staticMode={printMode||readOnly}/>:<StructuredFields config={block.responseFields!} value={value} onChange={onChange} disabled={disabled||readOnly} staticMode={printMode||readOnly}/>}</>;
  if(block.type==='measurement_table'&&block.calibrationSeries)return <CalibrationTable block={block} concentrationValue={allAnswers[block.concentrationSource?.blockId??""]} volumeValue={allAnswers[block.volumeSourceBlockId??""]} value={value} mode={measurementMode(block,mode)} disabled={disabled} onChange={onChange}/>;
  if(block.type==='graph_task'&&block.graphyo)return <><p><MathText text={block.prompt}/></p>{block.graphyo.classTable?<SharedGraphyoActivity shared={sharedGraphyo} key={block.id} config={block.graphyo} value={value} disabled={disabled||readOnly||printMode} readOnly={graphyoReadOnly||readOnly||printMode} onChange={onChange}/>:<GraphyoActivity key={block.id} config={block.graphyo} value={value} disabled={disabled||readOnly||printMode} readOnly={graphyoReadOnly||readOnly||printMode} onChange={onChange}/>}</>;
  if(block.type==='graph_task'&&block.graphConfig)return <><p><MathText text={block.prompt}/></p><InteractiveGraph renderMode={printMode?'static':'interactive'} config={block.graphConfig} sourcePoints={graphSourcePoints(block,allBlocks,allAnswers,mode)} value={value} disabled={disabled} onChange={onChange}/></>;
  if(block.type==='graph_task') {const source=allBlocks.find(b=>b.id===block.sourceTableBlockId);if(source?.type==='measurement_table'&&source.calibrationSeries)return <CalibrationGraph block={block} rows={calibrationRows(source,allAnswers[source.id]??'',mode,allAnswers[source.concentrationSource?.blockId??''])} value={value} disabled={disabled} onChange={onChange}/>;}
  if((block.type==='question'||block.type==='measurement_table')&&block.resourceMeasurementTable){
    const linkedSource=block.type==='question'&&!!block.measurementSourceBlockId;
    const measurementValue=linkedSource
      ? measurementAnswerFromSource(value,allAnswers[block.measurementSourceBlockId!]??'')
      : value;
    return <><p><MathText text={block.prompt}/></p><ResourceMeasurementAnswer kind={block.measurementTableKind} acquisitionOnly={block.type==='measurement_table'&&block.acquisitionOnly} linkedSource={linkedSource} value={measurementValue} disabled={disabled} onChange={onChange}/></>;
  }
  if (block.type === "measurement_table")
    return (
      <MeasurementAnswer
        block={block}
        value={value}
        disabled={disabled}
        onChange={onChange}
      />
    );
  if (block.type !== "question")
    return (
      <label>
        <MathText text={"prompt" in block ? block.prompt : "Compte rendu"} />
        <textarea
          disabled={disabled}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      </label>
    );
  return (
    <fieldset disabled={disabled}>
      <legend>
        <MathText text={block.prompt} />
        {block.required ? " *" : ""}
      </legend>
      {block.answerType === "mcq" ? (
        (block.options ?? []).map((o) => {
          let ids: string[] = [];
          try {
            ids = JSON.parse(value || "[]");
          } catch {
            /* legacy text answer */
          }
          return (
            <label className="library-check" key={o.id}>
              <input
                type={block.selectionMode === "single" ? "radio" : "checkbox"}
                name={block.id}
                checked={ids.includes(o.id)}
                onChange={(e) =>
                  onChange(
                    JSON.stringify(
                      e.target.checked
                        ? block.selectionMode === "single" ? [o.id] : [...ids, o.id]
                        : ids.filter((id) => id !== o.id),
                    ),
                  )
                }
              />
              <MathText text={o.text} />
            </label>
          );
        })
      ) : block.answerType === "true_false" ? (
        <select
          aria-label={formatChemistryText(block.prompt)}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">Choisir…</option>
          <option value="true">Vrai</option>
          <option value="false">Faux</option>
        </select>
      ) : (printMode || readOnly) ? <p className="pdf-answer-value"><MathText text={value || "Aucune réponse."}/></p> : block.answerType === "numeric" ? <label><span>Valeur {block.answerUnit ? `(${block.answerUnit})` : "et unité"}</span><input aria-label={formatChemistryText(block.prompt)} inputMode="decimal" value={value} placeholder={block.answerUnit ? `Valeur en ${block.answerUnit}` : "Valeur et unité"} onChange={e=>onChange(e.target.value)}/></label> : block.answerType === "short_text" || block.answerType === "formula" ? <input aria-label={formatChemistryText(block.prompt)} value={value} onChange={e=>onChange(e.target.value)}/> : (
        <textarea
          className="library-student-answer"
          aria-label={formatChemistryText(block.prompt)}
          rows={block.answerType === "open" ? 4 : 2}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
      {!printMode&&!readOnly&&!['mcq','true_false','numeric'].includes(block.answerType)&&value&&formatChemistryText(value)!==value&&<p className="chemical-input-preview" aria-label="Aperçu de la notation chimique"><MathText text={value}/></p>}
      {block.hint && !block.helpSnapshotSourceBlockId && (
        <details>
          <summary>Indice</summary>
          <MathText text={block.hint} />
        </details>
      )}
    </fieldset>
  );
}

export function LibraryWork({
  version,
  attempt,
  teacher = false,
  busy = false,
  execute,
  onBack,
  onRefresh,
  readOnly = false,
  access = "active",
  activeUntil,
  localPreview = false,
  printMode = false,
  projectionCorrection = false,
  attachmentsEnabled = true,
  referenceUpgradesEnabled = true,
}: {
  version: ActivityVersion;
  attempt?: StudentAttempt;
  teacher?: boolean;
  readOnly?: boolean;
  access?: "active" | "paused" | "ended";
  activeUntil?: string;
  localPreview?: boolean;
  printMode?: boolean;
  /** Teacher-only presentation mode: activity without any student copy, with corrections revealed question by question. */
  projectionCorrection?: boolean;
  /** External individual runs disable photo uploads until their dedicated upload contract is implemented. */
  attachmentsEnabled?: boolean;
  /** A pinned external run must keep the exact correction of its assigned version. */
  referenceUpgradesEnabled?: boolean;
  busy?: boolean;
  execute?: (c: LibraryCommand) => Promise<unknown>;
  onBack: () => void;
  onRefresh?: () => Promise<void>;
}) {
  const [vinegarTemplate,setVinegarTemplate]=useState<import('./interactive-activity-package').InteractiveActivityPackage|null>(null);
  useEffect(()=>{
    let cancelled=false;
    if(!referenceUpgradesEnabled||!(teacher||projectionCorrection)||!/vinaigre/i.test(version.title)){setVinegarTemplate(null);return;}
    void import('./library-reference-activities').then(({referenceSources})=>{if(!cancelled)setVinegarTemplate(referenceSources.find(r=>r.id==='reference-tp6-vinaigre')!.source as unknown as import('./interactive-activity-package').InteractiveActivityPackage);});
    return()=>{cancelled=true;};
  },[referenceUpgradesEnabled,teacher,projectionCorrection,version.id,version.title]);
  const renderVersion = useMemo(() => {
    const base=withCurrentAttenuationRuntimeBlocks(withCurrentDopplerRuntimeAssets(version));
    if(!vinegarTemplate||!referenceUpgradesEnabled||!(teacher||projectionCorrection)||!base.package)return base;
    const patched=patchVinegarPresentation(activityPackageForEditing(base),vinegarTemplate);
    return patched?{...base,package:patched,...(base.blocks?{blocks:patched.interactive.blocks}:{})}:base;
  }, [version,vinegarTemplate,referenceUpgradesEnabled,teacher,projectionCorrection]);
  const content = useMemo(() => normalizeActivity(renderVersion), [renderVersion]);
  const serverAnswerIds=useMemo(()=>content.blocks.filter(b=>(b.type==='simulation'||b.type==='animation')&&b.resourceId===chemicalLaboratoryId).map(b=>b.id),[content]);
  const acknowledgedAttempt=useRef<StudentAttempt|undefined>(undefined);
  const [answers, setAnswers] = useState(attempt?.answers ?? {});
  const baseAnswers = useRef(attempt?.answers ?? {});
  const saving = useRef<Promise<StudentAttempt | undefined> | null>(null);
  const sentAnswers = useRef<Record<string, string> | null>(null);
  const [autoSaving, setAutoSaving] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [answerConflict, setAnswerConflict] = useState(false);
  const [activeCorrectionGroupId, setActiveCorrectionGroupId] = useState("");
  const [bulkGradeByCheckpoint, setBulkGradeByCheckpoint] = useState<Record<string, boolean>>({});
  const [projectionOpen, setProjectionOpen] = useState<Record<string, boolean>>({});
  const correctionTabs = useRef<HTMLElement>(null);
  useEffect(() => {
    const incoming = latestAcknowledgedAttempt(attempt,acknowledgedAttempt.current)?.answers ?? {};
    if (attempt?.copyAccessible === false && !teacher) {setAnswers({}); baseAnswers.current = {}; setAnswerConflict(false); return;}
    const local = {...answers};
    for(const id of serverAnswerIds){if(Object.hasOwn(incoming,id))local[id]=incoming[id];else delete local[id];}
    if (attempt && !teacher) for (const id of Object.keys(local)) if (evidenceLocked(attempt,id)) local[id] = incoming[id] ?? "";
    if (JSON.stringify(incoming) !== JSON.stringify(baseAnswers.current)) {
      // Our own acknowledgement must not conflict with typing performed during the request.
      const acknowledged = sentAnswers.current && Object.entries(sentAnswers.current).every(([id, value]) => incoming[id] === value);
      const baseline = acknowledged ? {...baseAnswers.current, ...sentAnswers.current} : baseAnswers.current;
      const merged = reconcileAnswers(baseline, local, incoming, serverAnswerIds);
      setAnswers(merged.answers);
      if (merged.conflicts.length) setAnswerConflict(true);
      else baseAnswers.current = incoming;
      if (acknowledged) sentAnswers.current = null;
    } else if (JSON.stringify(local)!==JSON.stringify(answers)) {setAnswers(local);setSaveFailed(false);}
  }, [attempt?.revision]);
  const [clock, setClock] = useState(Date.now());
  useEffect(() => {
    if (!activeUntil) return;
    const timer = setInterval(() => setClock(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [activeUntil]);
  const accessOpen = access === "active" && (!activeUntil || clock < Date.parse(activeUntil));
  const [results, setResults] = useState<
    Record<string, { level: CompetenceLevel; comment: string }>
  >(() =>
    Object.fromEntries(
      Object.entries(attempt?.competenceResults ?? {}).map(([id, r]) => [
        id,
        { level: r.level, comment: r.comment },
      ]),
    ),
  );
  const [feedback, setFeedback] = useState(attempt?.feedback.join("\n") ?? "");
  const [grade, setGrade] = useState(attempt?.grade?.toString() ?? "");
  const [notice, setNotice] = useState("");
  const [openingHelp, setOpeningHelp] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const helpPending = useRef(false);
  const [openedHelp, setOpenedHelp] = useState<Record<string, boolean>>({});
  const [previewHelpLevels,setPreviewHelpLevels]=useState<Record<string,number>>({});
  const [confirmedHelp, setConfirmedHelp] = useState<Record<string, HelpUsage>>({});
  const helpUsage = {...attempt?.helpUsage};
  for (const [id, usage] of Object.entries(confirmedHelp)) {
    if (!helpUsage[id] || (usage.maxHelpLevel ?? 0) > (helpUsage[id].maxHelpLevel ?? 0)) helpUsage[id] = usage;
  }
  const container = useRef<HTMLDivElement>(null);
  const current = useRef({ attempt, execute, busy, answers });
  current.current = { attempt:latestAcknowledgedAttempt(attempt,acknowledgedAttempt.current), execute, answers, busy: busy || openingHelp || photoBusy || autoSaving || leaving };
  const demoOnly = import.meta.env?.DEV === true && localPreview;
  const focus = useEvaluationFocus(demoOnly?undefined:attempt, !demoOnly && !!attempt && !!attemptSessionId(attempt) && attempt.status === "in_progress" && !teacher && !readOnly && accessOpen, onRefresh);
  const writable =
    !!attempt && attempt.status === "in_progress" && !teacher && !readOnly && accessOpen && !answerConflict && !focus.blocked;
  const exitDraft = useRef({writable, answers, attempt, execute, serverAnswerIds});
  exitDraft.current = {writable, answers, attempt:current.current.attempt, execute, serverAnswerIds};
  useEffect(() => () => {
    // Switching dashboard tabs also unmounts the reader; flush its last local edit.
    const snapshot = exitDraft.current;
    if (!snapshot.writable || !snapshot.attempt || !snapshot.execute) return;
    void (async () => {
      try {
        const saved = saving.current ? await saving.current : undefined;
        const patch = changedAnswers(saved?.answers ?? baseAnswers.current, snapshot.answers, snapshot.serverAnswerIds);
        const a = saved ?? snapshot.attempt!;
        if (Object.keys(patch).length) await snapshot.execute!({type: "answer", attemptId: a.id, expectedRevision: a.revision, answers: patch});
      } catch { /* A revision conflict must never overwrite another member's work. */ }
    })();
  }, []);

  const documentBlockClasses = useMemo(() => {
    const result: Record<string, string> = {};
    let group: string[] = [];
    const flush = () => {
      if (!group.length) return;
      group.forEach((id, index) => {
        const position =
          group.length === 1 ? "single" :
          index === 0 ? "start" :
          index === group.length - 1 ? "end" : "middle";
        result[id] = `library-document-part library-document-${position}`;
      });
      group = [];
    };
    for (const item of content.flow) {
      if (item.type === "checkpoint") { flush(); continue; }
      const block = item.block;
      if (block.type === "section" || isEvidenceBlock(block)) { flush(); continue; }
      const documentStart =
        block.type === "text" &&
        typeof block.title === "string" &&
        /^Document\s+\d+/i.test(block.title.trim());
      if (documentStart) { flush(); group = [block.id]; continue; }
      if (group.length) group.push(block.id);
    }
    flush();
    return result;
  }, [content.flow]);
  const [referenceCorrections, setReferenceCorrections] = useState<Record<string, ReferenceCorrection>>({});
  const [referenceStudentCorrections, setReferenceStudentCorrections] = useState<Record<string, string>>({});
  useEffect(() => {
    let cancelled = false;
    const supportedReference = version.id === "reference-doppler" || version.id === "reference-tp5-avancement" || version.id === "reference-tp4-ph-dilution";
    if (!referenceUpgradesEnabled || !(teacher || projectionCorrection) || !supportedReference) {
      setReferenceCorrections({});
      setReferenceStudentCorrections({});
      return;
    }
    void import("./library-reference-activities").then(({referenceSources}) => {
      const reference = referenceSources.find((item) => item.id === version.id);
      const sourceRevision = reference?.source.interactive.revision ?? 0;
      const storedRevision = version.package?.interactive.revision ?? 0;
      const needsLatest =
        sourceRevision > storedRevision ||
        (version.id === "reference-doppler" && content.blocks.some(
          (block) =>
            block.type === "question" &&
            !block.correction?.explanation?.trim(),
        ));
      if (!needsLatest) {
        if (!cancelled) {
          setReferenceCorrections({});
          setReferenceStudentCorrections({});
        }
        return;
      }
      const sourceBlocks = reference?.source.interactive.blocks ?? [];
      const corrections = Object.fromEntries(
        sourceBlocks
          .filter((block: any) => block.type === "question" && block.correction)
          .map((block: any) => [block.id, block.correction as ReferenceCorrection]),
      );
      const studentCorrections = Object.fromEntries(
        sourceBlocks
          .filter((block: any) => ["question","measurement_table"].includes(block.type) && typeof block.studentCorrection === "string" && block.studentCorrection.trim())
          .map((block: any) => [block.id, block.studentCorrection as string]),
      );
      if (!cancelled) {
        setReferenceCorrections(corrections);
        setReferenceStudentCorrections(studentCorrections);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [teacher, projectionCorrection, referenceUpgradesEnabled, version.id, version.version, version.package?.interactive.revision, content.blocks]);
  const correctionTabMode = !!teacher && !!attempt && attempt.status !== "completed";
  const correctionBlocks = useMemo(() => content.blocks.filter(isEvidenceBlock), [content]);
  const correctionGroups = useMemo(() => {
    const groups: Array<{
      id: string;
      checkpoints: ActivityCheckpoint[];
      blocks: typeof correctionBlocks;
    }> = [];
    let pendingBlocks: typeof correctionBlocks = [];
    let segment = 1;

    for (const item of content.flow) {
      if (item.type === "checkpoint") {
        const cp = version.checkpoints.find((checkpoint) => checkpoint.id === item.id);
        if (cp) {
          groups.push({
            id: `segment:${segment++}`,
            checkpoints: [cp],
            blocks: pendingBlocks,
          });
          pendingBlocks = [];
        }
        continue;
      }

      if (isEvidenceBlock(item.block)) pendingBlocks = [...pendingBlocks, item.block];
    }

    if (pendingBlocks.length) {
      groups.push({
        id: `segment:${segment}`,
        checkpoints: [],
        blocks: pendingBlocks,
      });
    }

    return groups.filter((group) => group.blocks.length || group.checkpoints.length);
  }, [content.flow, correctionBlocks, version.checkpoints]);
  const selectedCorrectionGroup = correctionTabMode
    ? correctionGroups.find((group) => group.id === activeCorrectionGroupId) ?? correctionGroups[0]
    : undefined;
  useEffect(() => {
    if (!correctionTabMode || !correctionGroups.length) return;
    if (!correctionGroups.some((group) => group.id === activeCorrectionGroupId))
      setActiveCorrectionGroupId(correctionGroups[0].id);
  }, [correctionTabMode, correctionGroups, activeCorrectionGroupId]);
  useEffect(()=>{
    if(!writable)return;
    const seeded:Record<string,string>={};
    for(const b of content.blocks){
      const mode=version.package?.interactive.experimentalModeOverrides?.[b.experimentalPart??'']??version.package?.interactive.experimentalMode;
      if(b.type==='measurement_table'&&b.calibrationSeries&&measurementMode(b,mode)==='provided_data'&&!Object.hasOwn(answers,b.id))
        seeded[b.id]=JSON.stringify({format:'resource-measures-v1',text:'Jeu de données fourni par le professeur.',rows:b.calibrationSeries.map(r=>({source:'manual',sourceBlockId:'provided-data',solutionId:r.solutionId,absorbance:String(r.absorbance??''),conductivity:String(r.conductivity??''),aliquotVolumeMl:String(r.samplingVolumeMl??'')}))});
    }
    if(Object.keys(seeded).length)setAnswers(a=>({...seeded,...a}));
  },[writable,content,answers,version]);
  async function saveAnswers() {
    if (saving.current) return saving.current;
    try{await flushGraphyoAnswers(container.current);}catch(error){setSaveFailed(true);setNotice((error as Error).message);throw error;}
    if (saving.current) return saving.current;
    const {attempt: a, execute: send, answers: draft} = current.current;
    if (!a || !send) return a;
    const patch = changedAnswers(baseAnswers.current, draft, serverAnswerIds);
    if (!Object.keys(patch).length) return a;
    sentAnswers.current = patch;
    setAutoSaving(true);
    setSaveFailed(false);
    const promise = (async () => {
      try {
        const result = await send({type: "answer", attemptId: a.id, expectedRevision: a.revision, answers: patch}) as {state: {attempts: StudentAttempt[]}};
        const saved = result.state.attempts.find(row => row.id === a.id);
        setNotice("Réponses enregistrées automatiquement.");
        return saved;
      } catch (error) {
        sentAnswers.current = null;
        setSaveFailed(true);
        setNotice(error instanceof Error ? error.message : "Sauvegarde impossible. Tes réponses restent dans ce formulaire.");
        throw error;
      } finally {saving.current = null; setAutoSaving(false);}
    })();
    saving.current = promise;
    return promise;
  }
  async function receiveChemicalAction(block:Extract<InteractiveActivityBlock,{type:'simulation'|'animation'}>,action:WorldAction){
    const target=block.measurementTargetBlockId;
    if(!attempt){
      const value=chemicalLabAction(block.resourceConfig!,current.current.answers[block.id]??'',action);
      const patch:Record<string,string>={[block.id]:value};
      if(target)patch[target]=mergeChemicalMeasurements(value,current.current.answers[target]??'',block.id);
      baseAnswers.current={...baseAnswers.current,...patch};current.current={...current.current,answers:{...current.current.answers,...patch}};setAnswers(a=>({...a,...patch}));return;
    }
    if(!writable||!execute)throw Error('Réalisation non modifiable.');
    const active=await saveAnswers()??current.current.attempt;if(!active)throw Error('Réalisation indisponible.');
    const result=await execute({type:'chemical_lab_action',attemptId:active.id,expectedRevision:active.revision,blockId:block.id,action}) as {state:{attempts:StudentAttempt[]}};
    const updated=result.state.attempts.find(a=>a.id===active.id)!;
    acknowledgedAttempt.current=latestAcknowledgedAttempt(updated,acknowledgedAttempt.current);
    const patch:Record<string,string>={[block.id]:acknowledgedAttempt.current!.answers[block.id]};
    if(target)patch[target]=acknowledgedAttempt.current!.answers[target];
    baseAnswers.current={...baseAnswers.current,...patch};current.current={...current.current,attempt:updated,answers:{...current.current.answers,...patch}};setAnswers(a=>({...a,...patch}));
  }
  async function receiveExperimentAction(block:Extract<InteractiveActivityBlock,{type:'simulation'|'animation'}>,action:ExperimentAction){
    const target=block.measurementTargetBlockId!;
    if(!attempt){const next=experimentAnswers(block.resourceConfig!,current.current.answers[block.id]??'',current.current.answers[target]??'',block.id,action,Date.now());current.current={...current.current,answers:{...current.current.answers,[block.id]:next.value,[target]:next.table}};setAnswers(a=>({...a,[block.id]:next.value,[target]:next.table}));return;}
    if(!writable||!execute)throw Error('Réalisation non modifiable.');
    const active=await saveAnswers()??current.current.attempt;if(!active)throw Error('Réalisation indisponible.');
    const result=await execute({type:'experiment_action',attemptId:active.id,expectedRevision:active.revision,blockId:block.id,action}) as {state:{attempts:StudentAttempt[]}};
    const updated=result.state.attempts.find(a=>a.id===active.id)!;
    const patch={[block.id]:updated.answers[block.id],[target]:updated.answers[target]};
    baseAnswers.current={...baseAnswers.current,...patch};current.current={...current.current,attempt:updated,answers:{...current.current.answers,...patch}};setAnswers(a=>({...a,...patch}));
  }
  async function receiveMeasurement(block:Extract<InteractiveActivityBlock,{type:'simulation'|'animation'}>,event:ExperimentEvent){
    const target=block.measurementTargetBlockId!;
    if(!attempt){
      if(event.type==='measurement_requested')throw new Error('Une réalisation est nécessaire.');
      const next=applyMeasurementEvent(current.current.answers[target]??'',block.id,block.resourceId!,block.resourceConfig??{},event);
      current.current={...current.current,answers:{...current.current.answers,[target]:next}};
      setAnswers(a=>({...a,[target]:next}));
      return sourceMeasurements(next,block.id);
    }
    if(!writable||!execute||!attempt||evidenceLocked(attempt,target))throw new Error('Preuve verrouillée ou réalisation non modifiable.');
    const saved=await saveAnswers();
    const active=saved??current.current.attempt;
    if(!active||evidenceLocked(active,target))throw new Error('Preuve verrouillée.');
    const value=current.current.answers[target]??active.answers[target]??'';
    const next=event.type==='measurement_requested'?value:applyMeasurementEvent(value,block.id,block.resourceId!,block.resourceConfig??{},event);
    if(next===value&&event.type!=='measurement_requested')return sourceMeasurements(value,block.id);
    const result=await execute(event.type==='measurement_requested'?{type:'measure_instrument',attemptId:active.id,expectedRevision:active.revision,blockId:block.id,solutionId:event.payload.solutionId,wavelengthNm:event.payload.wavelengthNm}:{type:'answer',attemptId:active.id,expectedRevision:active.revision,answers:{[target]:next}}) as {state:{attempts:StudentAttempt[]}};
    const updated=result.state.attempts.find(a=>a.id===active.id)!;
    baseAnswers.current={...baseAnswers.current,[target]:updated.answers[target]};
    current.current={...current.current,attempt:updated,answers:{...current.current.answers,[target]:updated.answers[target]}};
    setAnswers(a=>({...a,[target]:updated.answers[target]}));
    return sourceMeasurements(updated.answers[target],block.id);
  }
  useEffect(() => {
    if (!writable || busy || openingHelp || photoBusy || autoSaving || leaving || saveFailed || !Object.keys(changedAnswers(baseAnswers.current, answers, serverAnswerIds)).length) return;
    const timer = setTimeout(() => void saveAnswers().catch(() => {}), 900);
    return () => clearTimeout(timer);
  }, [answers, writable, busy, openingHelp, photoBusy, autoSaving, leaving, saveFailed, attempt?.revision]);
  useEffect(() => {
    if (demoOnly || !attempt || teacher || readOnly || !writable) return;
    const query = new URLSearchParams({attemptId: attempt.id});
    const ping = (visible: boolean) => fetch(`/api/activity-library/live?${query}`, {method: "POST", credentials: "include", keepalive: true,
      headers: {"Content-Type": "application/json"}, body: JSON.stringify({visible})}).catch(() => {});
    const update = () => {void ping(document.visibilityState === "visible");};
    update(); const timer = setInterval(update, 15000);
    document.addEventListener("visibilitychange", update);
    return () => {clearInterval(timer); document.removeEventListener("visibilitychange", update); void ping(false);};
  }, [attempt?.id, teacher, readOnly, writable]);
  useEffect(() => {
    if (!writable || !Object.keys(changedAnswers(baseAnswers.current, answers, serverAnswerIds)).length) return;
    const warn = (event: BeforeUnloadEvent) => {event.preventDefault(); event.returnValue = "";};
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [writable, answers]);
  const liveSessionMode = !!teacher && !!attempt && !!attemptSessionId(attempt) &&
    ["not_started", "in_progress"].includes(attempt.status);
  const liveTracking = liveSessionMode && attempt ? trackingDetails(renderVersion, attempt) : null;
  const canReview =
    !liveSessionMode &&
    teacher &&
    attempt &&
    attempt.status !== "completed" &&
    (attempt.status === "submitted" ||
      attempt.status === "reviewed" ||
      content.capabilities.supportsDirectEvaluation);
  const finalized = !!attempt && (teacher ? attempt.status === "completed" : copyReleased(attempt));
  const pkg = useMemo(() => content.blocks.length ? activityPackageForEditing(renderVersion) : null, [renderVersion, content]);
  const reviewChanged =
    !!attempt &&
    (Object.keys(results).length !==
      Object.keys(attempt.competenceResults).length ||
      Object.entries(results).some(
        ([id, result]) =>
          result.level !== attempt.competenceResults[id]?.level ||
          result.comment !== attempt.competenceResults[id]?.comment,
      ) ||
      feedback !== attempt.feedback.join("\n") ||
      grade !== (attempt.grade?.toString() ?? ""));
  const checkpoint = (cp: ActivityCheckpoint) => {
    const displayedResult = attempt?.competenceResults[cp.id] ?? (attempt?.ownerType !== "group" && attempt ? attempt.attendance?.[attempt.studentId]?.results[cp.id] : undefined);
    const liveCheckpoint = liveTracking?.checkpoints.find((item) => item.id === cp.id);
    const liveLocked = !!attempt?.checkpointValidations?.[cp.id]?.locked;
    const display = pkg?.interactive.competencyCheckpoints?.find(
      (c) => c.id === cp.id,
    )?.studentDisplay;
    return (
      <article className="library-checkpoint" key={cp.id}>
        {teacher && attempt && attempt.status !== "completed" && attempt.checkpointValidations?.[cp.id]?.locked && <button disabled={busy || reviewChanged} onClick={async()=>{
          try {await execute?.({type:"unlock_checkpoint",attemptId:attempt.id,expectedRevision:attempt.revision,checkpointId:cp.id});setResults(previous=>{const next={...previous};delete next[cp.id];return next;});setNotice("Checkpoint déverrouillé. La trace de validation est conservée.");}
          catch(e){setNotice(e instanceof Error?e.message:"Déverrouillage impossible.");}
        }}>Déverrouiller {cp.competenceCode}</button>}
        {(teacher || display?.showCompetenceName !== false) && (
          <h3>
            {cp.competenceCode} — {cp.competenceLabel}
          </h3>
        )}
        {teacher && <p>
          {checkpointEvidenceIds(cp).length
            ? `Preuves : ${checkpointEvidenceIds(cp).map((id) => pkg ? questionLabel(pkg, id) : id).join(", ")}`
            : "Observation de la démarche"}
        </p>}
        {liveSessionMode && attempt && !liveLocked && (
          <fieldset className="library-grade-picker" disabled={busy || attempt.status !== "in_progress" || !liveCheckpoint?.reached}>
            <legend>Validation live de {cp.competenceCode}</legend>
            <div className="library-grade-grid">
              {grades.map((g) => (
                <button
                  type="button"
                  key={g}
                  className="library-grade-choice"
                  onClick={async () => {
                    try {
                      await execute?.({type:"validate_checkpoint", attemptId:attempt.id, expectedRevision:attempt.revision, checkpointId:cp.id, level:g});
                      setResults((current) => ({...current, [cp.id]: {level:g, comment:""}}));
                      setNotice(`${cp.competenceCode} validée en direct : ${gradeLabel(g)}.`);
                    } catch (e) {
                      setNotice(e instanceof Error ? e.message : "Validation live impossible.");
                    }
                  }}
                >
                  <span className="library-grade-label">{gradeLabel(g)}</span>
                </button>
              ))}
            </div>
            {!liveCheckpoint?.reached && <small>La validation sera disponible dès que toutes les preuves de ce checkpoint seront renseignées.</small>}
          </fieldset>
        )}
        {!canReview && (teacher || display?.showCriteria !== false) && (
          <dl className="library-competency-rubric">
            {Object.entries(cp.rubric).map(([level, criterion]) => {
              const selected = !teacher && finalized && displayedResult?.level === level;
              return <div key={level} className={selected ? `selected level-${level.toLowerCase()}` : ""}>
                <dt>{level}</dt>
                <dd><MathText text={criterion} /></dd>
              </div>;
            })}
          </dl>
        )}
        {canReview ? (
          <>
            {attempt.attendance && Object.values(attempt.attendance).some(r=>r.results[cp.id]) && <p>
              {attempt.ownerType === "group" ? "Certains membres ont un résultat individuel Absent automatique : il sera conservé lors de la finalisation du résultat commun." : "Absent automatique (présence en séance). Une sélection manuelle ci-dessous remplacera ce résultat."}
            </p>}
            <fieldset className="library-grade-picker" disabled={busy || openingHelp}>
              <legend>Validation de {cp.competenceCode}</legend>
              <div className="library-grade-scope" role="radiogroup" aria-label={`Portée de la note pour ${cp.competenceCode}`}>
                <button
                  type="button"
                  role="radio"
                  aria-checked={!bulkGradeByCheckpoint[cp.id]}
                  onClick={() => setBulkGradeByCheckpoint((current) => ({...current, [cp.id]: false}))}
                >
                  {attempt.ownerType === "group" ? "Ce groupe" : "Cet élève"}
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={!!bulkGradeByCheckpoint[cp.id]}
                  onClick={() => setBulkGradeByCheckpoint((current) => ({...current, [cp.id]: true}))}
                >
                  Tous les présents
                </button>
              </div>
              <div className="library-grade-grid">
                {grades.map((g) => {
                  const bulk = !!bulkGradeByCheckpoint[cp.id];
                  const selected = (results[cp.id]?.level ?? (attempt.ownerType !== "group" ? attempt.attendance?.[attempt.studentId]?.results[cp.id]?.level : undefined)) === g;
                  const criterion = g === "NE" || g === "ABS" ? undefined : cp.rubric[g];
                  return (
                    <button
                      type="button"
                      key={g}
                      className="library-grade-choice"
                      aria-pressed={selected}
                      disabled={bulk && g === "ABS"}
                      title={bulk && g === "ABS" ? "Le mode Tous les présents ne modifie jamais les absents." : undefined}
                      onClick={async () => {
                        if (!bulk) {
                          setResults((current) => ({
                            ...current,
                            [cp.id]: {comment: current[cp.id]?.comment ?? "", level: g},
                          }));
                          return;
                        }
                        if (g === "ABS") return;
                        if (!window.confirm(`Attribuer ${gradeLabel(g)} à tous les présents pour ${cp.competenceCode} ? Les notes déjà saisies pour cette compétence seront remplacées.`)) return;
                        try {
                          await execute?.({
                            type: "bulk_checkpoint_grade",
                            activityId: attempt.activityId,
                            activityVersion: attempt.activityVersion,
                            classId: attempt.classId,
                            checkpointId: cp.id,
                            level: g,
                          });
                          setResults((current) => ({
                            ...current,
                            [cp.id]: {comment: current[cp.id]?.comment ?? "", level: g},
                          }));
                          setNotice(`${gradeLabel(g)} attribué à tous les présents pour ${cp.competenceCode}.`);
                        } catch (e) {
                          setNotice(e instanceof Error ? e.message : "Attribution collective impossible.");
                        }
                      }}
                    >
                      <span className="library-grade-label">{gradeLabel(g)}</span>
                      {criterion && <span><MathText text={criterion} /></span>}
                      <span className="library-grade-selection" aria-hidden="true">
                        {bulk && g === "ABS" ? "Indisponible en mode collectif" : selected ? "✓ Sélectionné" : "Sélectionner"}
                      </span>
                    </button>
                  );
                })}
              </div>
            </fieldset>
            <label>
              Commentaire pour {cp.competenceCode}
              <textarea
                value={results[cp.id]?.comment ?? ""}
                onChange={(e) =>
                  setResults({
                    ...results,
                    [cp.id]: {
                      level: results[cp.id]?.level ?? "NE",
                      comment: e.target.value,
                    },
                  })
                }
              />
            </label>
          </>
        ) : (
          displayedResult && (
            teacher ? <p><strong>{gradeLabel(displayedResult.level)}</strong>{displayedResult.comment && ` · ${displayedResult.comment}`}</p>
            : ["NE","ABS"].includes(displayedResult.level)
              ? <p className={`student-competency-result level-${displayedResult.level.toLowerCase()}`}><strong>{gradeLabel(displayedResult.level)}</strong>{displayedResult.comment && ` · ${displayedResult.comment}`}</p>
              : displayedResult.comment
                ? <p className="student-competency-comment"><strong>Commentaire du professeur :</strong> {displayedResult.comment}</p>
                : null
          )
        )}
      </article>
    );
  };
  async function openHelp(blockId: string, sourceId: string, helpLevel?:number) {
    if (helpPending.current || busy || photoBusy || autoSaving) return;
    if (helpLevel && (teacher || !attempt)) {
      setPreviewHelpLevels(previous=>({...previous,[blockId]:helpLevel}));
      return;
    }
    if ((!helpLevel && helpUsage[blockId]?.helpUsed) || teacher || !attempt) {
      setOpenedHelp(previous => ({...previous, [blockId]: !previous[blockId]}));
      return;
    }
    if (!writable || !execute) return;
    helpPending.current = true;
    setOpeningHelp(true);
    try {
      const saved = await execute({type: "open_help", attemptId: attempt.id, expectedRevision: attempt.revision,
        blockId, helpLevel, sourceAnswer: answers[sourceId] ?? "", answers}) as {state: {attempts: StudentAttempt[]}};
      const usage = saved?.state?.attempts.find(a => a.id === attempt.id)?.helpUsage?.[blockId];
      if (!usage?.helpUsed) throw new Error("L’ouverture de l’aide n’a pas été confirmée. Réessaie.");
      setConfirmedHelp(previous => ({...previous, [blockId]: usage}));
      setOpenedHelp(previous => ({...previous, [blockId]: true}));
      setNotice(helpLevel ? "Ouverture de l’aide enregistrée. Vous pouvez poursuivre votre réponse." : "Proposition initiale figée ; ouverture de l’aide enregistrée.");
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Enregistrement impossible. L’aide reste fermée.");
    } finally {
      helpPending.current = false;
      setOpeningHelp(false);
    }
  }
  async function run(command: LibraryCommand) {
    try {
      await execute?.(command);
      setNotice("Enregistré.");
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "Enregistrement impossible.");
    }
  }
  useEffect(() => {
    if (!writable) return;
    const listener = (event: MessageEvent) => {
      const { attempt: a, execute: send, busy: pending } = current.current;
      if (!a || pending || !send || a.status !== "in_progress") return;
      const frame = Array.from(
        container.current?.querySelectorAll("iframe") ?? [],
      ).find((f) => f.contentWindow === event.source);
      if (!frame) return;
      // Sandboxed resources have an opaque origin; bind to the exact iframe window.
      const opaque =
        frame.hasAttribute("sandbox") &&
        !frame.sandbox.contains("allow-same-origin");
      if (
        event.origin !==
        (opaque ? "null" : new URL(frame.src, location.href).origin)
      )
        return;
      const b = frame.closest<HTMLElement>("[data-evidence-source]")?.dataset
        .evidenceSource;
      const data = event.data;
      if (
        !b ||
        data?.type !== "competenceo:evidence" ||
        typeof data.value !== "string" ||
        !version.competenceIds.includes(data.competencyId)
      )
        return;
      void send({
        type: "evidence",
        attemptId: a.id,
        expectedRevision: a.revision,
        evidence: {
          id: crypto.randomUUID(),
          sourceId: b,
          competencyId: data.competencyId,
          value: data.value.slice(0, 1000),
          kind: "interaction",
        },
      }).catch(() => {});
    };
    window.addEventListener("message", listener);
    return () => window.removeEventListener("message", listener);
  }, [writable, version]);
  if (!teacher && attempt && (attempt.copyAccessible === false || (!copyReleased(attempt) && (access === "ended" || ["submitted", "reviewed", "completed"].includes(attempt.status))))) return <section className="library-work">
    <button onClick={onBack}>← Retour</button><h1><MathText text={version.title}/></h1>
    <p>{attempt.status === "completed" ? "Corrigé — en attente de restitution" : "En correction"}</p>
    <p>La copie sera consultable après restitution par le professeur.</p>
    {version.checkpoints.map(cp => <p key={cp.id}>{cp.competenceCode} · {attempt.competenceResults[cp.id] ? gradeLabel(attempt.competenceResults[cp.id].level) : "Non encore évaluée"}</p>)}
  </section>;

  return (
    <section className={`library-work${projectionCorrection ? " correction-projection" : ""}`} data-attempt-id={attempt?.id} data-activity-id={version.id} data-activity-version={version.version} ref={container} onClickCapture={e=>{if((e.target as Element).closest("a[target='_blank']"))focus.allowExternal();}}>
      {focus.blocked && <div role="alert"><h1><MathText text={version.title}/></h1><p>Activité bloquée après une sortie de la fenêtre. Seul le professeur peut la débloquer. Les réponses enregistrées sont conservées.</p><p>Garde cette page ouverte : la manipulation reste conservée jusqu’au déblocage.</p>{focus.error && <p>{focus.error}</p>}</div>}
      <div hidden={focus.blocked} inert={focus.blocked}>
      <button disabled={autoSaving || leaving || busy || openingHelp || photoBusy} onClick={async () => {
        if (writable) {setLeaving(true); try {await saveAnswers();} catch {setLeaving(false); return;}}
        if (answerConflict) {setNotice("Résous le conflit de réponses avant de quitter la copie."); return;}
        exitDraft.current.writable = false; // The explicit Back action has already flushed the draft.
        onBack();
      }}>← Retour</button>
      {onRefresh && <button disabled={busy || autoSaving || openingHelp || photoBusy} onClick={() => {setSaveFailed(false); void onRefresh();}}>Actualiser le travail partagé</button>}
      {writable && !demoOnly && !!attemptSessionId(attempt!) && <p className="evaluation-focus-notice">Évaluation surveillée : quitter cet onglet ou cette fenêtre bloque la copie jusqu’au déblocage par le professeur. Utilise les boutons prévus pour les photos et les ressources.</p>}
      {writable && <p role="status">{autoSaving ? "Sauvegarde…" : saveFailed ? "Réponses non sauvegardées — réessaie avec Enregistrer mes réponses." : Object.keys(changedAnswers(baseAnswers.current, answers, serverAnswerIds)).length ? "Modifications en attente de sauvegarde…" : "Réponses enregistrées."}</p>}
      {answerConflict && <p role="alert">Une autre sauvegarde a modifié les réponses. Tes modifications locales ne seront pas envoyées.
        <button onClick={() => { setAnswers(attempt?.answers ?? {}); baseAnswers.current = attempt?.answers ?? {}; setAnswerConflict(false); }}>Reprendre les réponses enregistrées et abandonner mes modifications locales</button>
      </p>}
      <p className="library-breadcrumb">
        {version.levelTitle} / {version.themeTitle} / {version.chapterTitle}
      </p>
      <h1><MathText text={version.title}/></h1>
      {projectionCorrection && <div className="correction-projection-banner">
        <strong>Mode projection — correction</strong>
        <span>Les corrections sont masquées au départ. Ouvre-les question par question pendant la mise en commun.</span>
      </div>}
      {attempt?.ownerType === "group" && <p>Travail commun du {attempt.groupName} · {attempt.memberIdsSnapshot?.length} membres · une seule copie.</p>}
      {!teacher && !accessOpen && access !== "ended" && <p role="status">Séance en pause. L’accès reprendra automatiquement lorsque le professeur relancera la séance.</p>}
      {teacher && <p>
        Version {version.version} ·{" "}
        {attempt ? studentWorkStatus(attempt.status) : "Prévisualisation"}
      </p>}
      {teacher && finalized && (
        <p>
          Terminée le {new Date(attempt.completedAt!).toLocaleString("fr-FR")} ·
          Durée entre le début et la remise :{" "}
          {Math.round((attempt.durationSeconds ?? 0) / 60)} min{" "}
          {attempt.grade !== null && `· Note : ${attempt.grade}/20`}
        </p>
      )}
      {(teacher || printMode) && <SavedExperimentReports answers={answers}/> }
      {teacher&&content.blocks.filter(block=>(block.type==='simulation'||block.type==='animation')&&block.resourceId===chemicalLaboratoryId).map(block=><TransferJournal key={block.id} title={`Journal — ${block.title??"Laboratoire"}`} events={parseChemicalWorld(answers[block.id]??"")?.transferEvents}/>)}
      {teacher&&content.blocks.map(block=>{
        if((block.type!=='simulation'&&block.type!=='animation')||!block.lockAfterCompletion||!savedManipulation(answers[block.id]??''))return null;
        const asset=pkg?.interactive.assets.find(a=>a.id===block.assetId),resource=asset&&resourceForAsset(asset);
        return resource?<details key={block.id}><summary>Bilan validé — {block.title??resource.name}</summary><ValidatedManipulation resource={resource} config={block.resourceConfig} value={answers[block.id]} disabled onValidate={async()=>{}}/></details>:null;
      })}
      {correctionTabMode && correctionGroups.length > 0 && <nav ref={correctionTabs} className="teacher-correction-tabs" role="tablist" aria-label="Étapes de correction dans l’ordre de l’activité">
        {correctionGroups.map((group,index)=>{
          const finished=group.checkpoints.length
            ? group.checkpoints.every(cp=>Boolean(results[cp.id] ?? attempt.competenceResults[cp.id]))
            : group.blocks.every(block=>Boolean(attempt?.teacherTextAnnotations?.[block.id] || attempt?.teacherCorrectionOverrides?.[block.id] || attempt?.status==="reviewed"));
          const active=group.id===selectedCorrectionGroup?.id;
          const blockIndexes=group.blocks.map(block=>correctionBlocks.findIndex(candidate=>candidate.id===block.id)+1).filter(n=>n>0);
          const questionRange=blockIndexes.length
            ? blockIndexes.length===1
              ? `Q${blockIndexes[0]}`
              : `Q${blockIndexes[0]}–Q${blockIndexes[blockIndexes.length-1]}`
            : "Validation";
          const competenceLabel=group.checkpoints.map(cp=>cp.competenceCode).join(" + ");
          return <button type="button" role="tab" key={group.id} aria-selected={active}
            className={finished?"finished":active?"active":""} onClick={()=>setActiveCorrectionGroupId(group.id)}>
            <strong>Onglet {index+1}</strong>
            <span>{questionRange}{competenceLabel ? ` · validation ${competenceLabel}` : ""}</span>
            {finished ? <small>Fini</small> : active ? <small>En cours</small> : null}
          </button>;
        })}
      </nav>}
      {content.flow.map((item) => {
        if (item.type === "checkpoint") {
          const cp=version.checkpoints.find(c => c.id === item.id)!;
          if (correctionTabMode && selectedCorrectionGroup && !selectedCorrectionGroup.checkpoints.some(groupCp => groupCp.id === cp.id)) return null;
          return checkpoint(cp);
        }
        const block = item.block;
        if (correctionTabMode) {
          const linkedExperiment=(block.type==='simulation'||block.type==='animation')&&block.resourceId===measurementEngineId&&(!selectedCorrectionGroup||selectedCorrectionGroup.blocks.some(b=>b.id===block.measurementTargetBlockId));
          if (!linkedExperiment&&(!isEvidenceBlock(block) || (selectedCorrectionGroup && !selectedCorrectionGroup.blocks.some(groupBlock => groupBlock.id === block.id)))) return null;
        }
        return (
        <Fragment key={block.id}>
          <article id={`activity-block-${block.id}`} data-evidence-source={block.id} data-pdf-resource-url={activityPdfResource(block,pkg?.interactive.assets)} data-pdf-resource-kind={block.type} className={documentBlockClasses[block.id]}>
            {teacher && attempt && evidenceLocked(attempt,block.id) && <p>Preuve validée — verrouillée par le professeur.</p>}
            {(block.type==='simulation'||block.type==='animation')&&block.resourceId===chemicalLaboratoryId ? <ChemicalLaboratory config={block.resourceConfig!} value={answers[block.id]??''} disabled={printMode||readOnly||!!teacher||finalized||busy||!!attempt&&(!writable||evidenceLocked(attempt,block.id)||!!block.measurementTargetBlockId&&evidenceLocked(attempt,block.measurementTargetBlockId))} onAction={action=>receiveChemicalAction(block,action)}/> : (block.type==='simulation'||block.type==='animation')&&block.resourceId===measurementEngineId ? <MeasurementAndTitrationEngine config={block.resourceConfig!} value={answers[block.id]??''} staticMode={printMode} correction={!!teacher||finalized} disabled={readOnly||!!teacher||finalized||busy||!!attempt&&(!writable||evidenceLocked(attempt,block.id)||evidenceLocked(attempt,block.measurementTargetBlockId!))} onAction={action=>receiveExperimentAction(block,action)}/> : printMode && ['simulation','animation','video'].includes(block.type) ? <><h3>{block.title}</h3>{'prompt' in block&&<p>{block.prompt}</p>}{('assetId' in block)&&savedManipulation(answers[block.id]??'')&&(()=>{const asset=pkg?.interactive.assets.find(a=>a.id===block.assetId),resource=asset&&resourceForAsset(asset);return resource?<ValidatedManipulation resource={resource} value={answers[block.id]} disabled onValidate={async()=>{}}/>:null;})()}</> : ["question", "measurement_table", "graph_task"].includes(
              block.type,
            ) ? (
              <>
                {block.title && <h3><MathText text={block.title} /></h3>}
                {block.type==='question' && (teacher||finalized) && <QuestionCorrection value={attempt?.questionVerdicts?.[block.id]?.state??automaticQuestionVerdict(block,answers[block.id]??'')} editable={!!teacher&&!!attempt&&!finalized} disabled={busy} onChange={async state=>{if(!execute||!attempt)throw new Error('Copie indisponible.');await execute({type:'set_question_verdict',attemptId:attempt.id,expectedRevision:attempt.revision,blockId:block.id,state});}}/>}

                {block.type==='question'&&block.measurementSourceBlockId&&<p><a href={`#activity-block-${block.measurementSourceBlockId}`}>Consulter le carnet de mesures</a></p>}
                <AnswerBlock key={`${attempt?.id??"preview"}:${version.id}:${version.version}:${block.id}`} graphyoReadOnly={!writable || (!!attempt && evidenceLocked(attempt,block.id)) || Object.values(helpUsage).some(h=>h.helpUsed&&!h.progressive&&h.sourceBlockId===block.id)} printMode={printMode} readOnly={readOnly||teacher||finalized}
                  block={block}
                  sharedGraphyo={attempt&&attemptSessionId(attempt)&&block.type==='graph_task'&&block.graphyo?.classTable?{
                    load:async()=>{
                      const {libraryRequest}=await import('./activity-library-client');
                      const response=await libraryRequest();
                      return response.state.digitalSessions?.find(s=>s.id===attemptSessionId(attempt))?.sharedGraphyo?.[block.id]??{};
                    },
                    publish:async cells=>{
                      const active=await saveAnswers()??current.current.attempt;
                      if(!active||!execute)throw Error('Réalisation indisponible.');
                      const result=await execute({type:'share_graphyo',attemptId:active.id,expectedRevision:active.revision,blockId:block.id,cells}) as import('./activity-library-client').LibraryResponse;
                      const updated=result.state.attempts.find(a=>a.id===active.id);
                      acknowledgedAttempt.current=latestAcknowledgedAttempt(updated,acknowledgedAttempt.current);
                      if(updated)current.current={...current.current,attempt:updated};
                      return result.state.digitalSessions?.find(s=>s.id===attemptSessionId(active))?.sharedGraphyo?.[block.id]??{};
                    },
                  }:undefined}
                  allAnswers={answers} allBlocks={content.blocks} mode={pkg?.interactive.experimentalModeOverrides?.[block.experimentalPart??""]??pkg?.interactive.experimentalMode??"real"}
                  value={answers[block.id] ?? ""}
                  disabled={!writable || busy || openingHelp || photoBusy || leaving || (!!attempt && evidenceLocked(attempt,block.id)) || Object.values(helpUsage).some(h => h.helpUsed && !h.progressive && h.sourceBlockId === block.id)}
                  onChange={(value) => {
                    current.current={...current.current,answers:{...current.current.answers,[block.id]:value}};
                    setAnswers(a=>({...a,[block.id]:value}));
                  }}
                />
                {!attempt && !printMode && attachmentsEnabled && allowsAnswerAttachmentsForActivity(version.id,block) && <LibraryAnswerPhotos
                  previewActivityKey={`preview:${version.id}`} blockId={block.id} writable disabled={busy || openingHelp || photoBusy}
                  beforeUpload={async()=>{}} onRefresh={async()=>{}} onActive={setPhotoBusy}
                />}
                {attempt && attachmentsEnabled && (allowsAnswerAttachmentsForActivity(version.id,block)||!!attempt.photos?.[block.id]?.length) && <LibraryAnswerPhotos
                  attemptId={attempt.id} blockId={block.id} photos={attempt.photos?.[block.id]}
                  annotations={teacher || finalized ? attempt.photoAnnotations : undefined}
                  canAnnotate={!!canReview}
                  onAnnotate={async (photoId, strokes) => {
                    if (!execute) throw new Error("Enregistrement indisponible.");
                    await execute({type: "annotate_photo", attemptId: attempt.id, expectedRevision: attempt.revision, photoId, strokes});
                  }}
                  canRotate={!!teacher}
                  onRotate={async photoId => {
                    if (!execute) throw new Error("Enregistrement indisponible.");
                    await execute({type: "rotate_photo", attemptId: attempt.id, expectedRevision: attempt.revision, photoId});
                  }}
                  writable={allowsAnswerAttachmentsForActivity(version.id,block) && writable && !evidenceLocked(attempt,block.id)}
                  disabled={busy || openingHelp || photoBusy || autoSaving}
                  onActive={setPhotoBusy}
                  onExternalInteraction={focus.allowExternal}
                  onRefresh={async () => { await onRefresh?.(); }}
                  beforeUpload={async () => {
                    if (!execute) throw new Error("Enregistrement indisponible.");
                    await saveAnswers();
                  }}
                />}
                {attempt && (teacher || finalized) && <TeacherRedTextAnnotation
                  value={attempt.teacherTextAnnotations?.[block.id]?.text ?? ""}
                  editable={!!teacher && ["in_progress","submitted","reviewed"].includes(attempt.status)}
                  busy={busy || openingHelp || photoBusy || autoSaving}
                  onSave={async text => {
                    if (!execute) throw new Error("Enregistrement indisponible.");
                    await execute({type:"annotate_text", attemptId:attempt.id, expectedRevision:attempt.revision, blockId:block.id, text});
                  }}
                />}
                {block.type === 'question' && block.progressiveHints?.length && <div className="library-tracked-help">
                  {teacher && <p>{helpUsage[block.id]?.helpUsed ? `Aides utilisées : ${helpUsage[block.id].maxHelpLevel} / ${block.progressiveHints.length}` : 'Aucune aide utilisée'}</p>}
                  {block.progressiveHints.slice(0,teacher||!attempt ? (previewHelpLevels[block.id]??0) : helpUsage[block.id]?.maxHelpLevel??0).map((hint,i)=><div key={i}><p><strong>Aide {i+1} : </strong><MathText text={hint}/></p>{block.choiceHints?.[i]&&<ChoiceHint config={block.choiceHints[i]} number={i+1} disabled={!!attempt&&!teacher&&(!writable||busy||openingHelp||photoBusy||autoSaving||leaving||evidenceLocked(attempt,block.id))}/>}</div>)}
                  {block.protocolOrder && !printMode && ((teacher||!attempt ? previewHelpLevels[block.id]??0 : helpUsage[block.id]?.maxHelpLevel??0)>=block.progressiveHints.length) && <ProtocolOrderGame key={block.id} config={block.protocolOrder} disabled={!!attempt&&!teacher&&(!writable||busy||openingHelp||photoBusy||autoSaving||leaving||evidenceLocked(attempt,block.id))} onUse={writable&&!teacher ? text=>setAnswers(a=>({...a,[block.id]:text})) : undefined}/>}
                  {((teacher||!attempt ? previewHelpLevels[block.id]??0 : helpUsage[block.id]?.maxHelpLevel??0)<block.progressiveHints.length)&&<button disabled={busy||openingHelp||photoBusy||autoSaving||!!attempt&&!teacher&&(!writable||evidenceLocked(attempt,block.id))} onClick={()=>void openHelp(block.id,block.id,(teacher||!attempt ? previewHelpLevels[block.id]??0 : helpUsage[block.id]?.maxHelpLevel??0)+1)}>Afficher l’aide suivante</button>}
                  {teacher&&helpUsage[block.id]?.helpUsed&&<details><summary>Proposition initiale et historique des aides</summary><p style={{whiteSpace:'pre-wrap'}}>{helpUsage[block.id].answerSnapshot}</p>{helpUsage[block.id].photoIdsSnapshot?.length ? <p>Photos présentes avant l’aide : {helpUsage[block.id].photoIdsSnapshot!.length}</p> : null}{helpUsage[block.id].helpHistory?.map(h=><p key={h.level}>Aide {h.level} — {new Date(h.openedAt).toLocaleString('fr-FR')}</p>)}<p>La réponse et les photos ajoutées ensuite constituent la proposition révisée.</p></details>}
                </div>}
                {block.type === "question" && block.helpSnapshotSourceBlockId && block.hint && (
                  <div className="library-tracked-help">
                    {teacher && <p>{helpUsage[block.id]?.helpUsed
                      ? `Aide consultée le ${new Date(helpUsage[block.id].helpOpenedAt).toLocaleString("fr-FR")}`
                      : "Aide non consultée dans le lecteur."}</p>}
                    <button disabled={busy || openingHelp || photoBusy || autoSaving || (!!attempt && !teacher && !writable && !helpUsage[block.id]?.helpUsed)}
                      onClick={() => void openHelp(block.id, block.helpSnapshotSourceBlockId!)}>
                      {openedHelp[block.id] ? "Masquer l’aide méthodologique" : "Afficher l’aide méthodologique"}
                    </button>
                    {openedHelp[block.id] && <p><MathText text={block.hint} /></p>}
                    {teacher && helpUsage[block.id]?.helpUsed && <details>
                      <summary>Proposition initiale enregistrée avant l’aide</summary>
                      <p style={{whiteSpace: "pre-wrap"}}>{helpUsage[block.id].answerSnapshot}</p>
                      {helpUsage[block.id].photoIdsSnapshot?.length ? <p>Photos présentes avant l’aide : {helpUsage[block.id].photoIdsSnapshot!.length}</p> : null}
                      <p>Les nouvelles photos ajoutées après l’aide sont conservées comme réponse révisée.</p>
                    </details>}
                  </div>
                )}
                {(teacher || finalized || projectionCorrection) &&
                  block.type === "question" && (() => {
                    const fallbackCorrection = referenceCorrections[block.id];
                    const correction = fallbackCorrection ?? block.correction;
                    const baseCorrection = referenceStudentCorrections[block.id] ?? block.studentCorrection ?? correction?.explanation ?? "";
                    const override = attempt?.teacherCorrectionOverrides?.[block.id]?.text;
                    const expectedOptions = block.options?.filter((option) => option.correct).map((option) => option.text) ?? [];
                    const structuredProgressCorrection = block.progressTable ? progressCorrectionAnswer(block.progressTable) : null;
                    const hasCorrection =
                      Boolean(structuredProgressCorrection) ||
                      Boolean((override ?? baseCorrection).trim()) ||
                      Boolean(block.correctionGraphConfig) ||
                      Boolean(block.correctionDiagram) ||
                      Boolean(block.diagramLabeling) ||
                      Boolean(block.correctionIllustrations?.length) ||
                      correction?.numericAnswer !== undefined ||
                      Boolean(correction?.acceptedAnswers?.length) ||
                      expectedOptions.length > 0;
                    if (!teacher && !projectionCorrection && !hasCorrection) return null;
                    const displayedCorrection = override ?? baseCorrection;
                    const progressCorrection = structuredProgressCorrection;
                    const result=projectionCorrection ? null : gradeActivityQuestion(block,answers[block.id]??'');
                    const body = <>
                      {result!==null&&<p>{result?'Réponse conforme à la référence.':'Réponse à revoir par rapport à la référence.'}</p>}
                      {progressCorrection && block.progressTable && <ReactionProgressTable
                        config={block.progressTable}
                        value={progressCorrection}
                        onChange={()=>{}}
                        staticMode
                        correctionMode
                      />}
                      {displayedCorrection && (!progressCorrection || !!override) && <PedagogicalCorrection text={displayedCorrection} />}
                      {block.diagramLabeling&&<ColorimetricTitrationDiagram config={block.diagramLabeling} value={encodeTitrationLabelingAnswer({kind:'titration-labeling',version:1,title:'',placements:block.diagramLabeling.expectedPlacements,validated:false})} onChange={()=>{}} staticMode correctionMode hideTitle/>}
                      {block.correctionDiagram && <TitrationDiagram config={block.correctionDiagram}/>}
                      {block.correctionGraphConfig && <StaticGraph config={block.correctionGraphConfig} audience="correction" constructionValues={block.correctionGraphConfig.constructions?.showValues}/>}
                      <DocumentMolecules items={block.correctionIllustrations}/>
                      {correction?.numericAnswer !== undefined && (
                        <p className="student-correction-answer">
                          Résultat numérique : <strong>{correction.numericAnswer} {correction.unit}</strong>
                        </p>
                      )}
                      {correction?.acceptedAnswers?.length ? (
                        <p><MathText text={correction.acceptedAnswers.join(", ")} /></p>
                      ) : null}
                      {projectionCorrection && expectedOptions.length > 0 && (
                        <p><strong>Réponse{expectedOptions.length > 1 ? "s" : ""} attendue{expectedOptions.length > 1 ? "s" : ""} :</strong>{" "}
                          <MathText text={expectedOptions.join(", ")} />
                        </p>
                      )}
                      {projectionCorrection && !hasCorrection && <p>Aucune correction n’est renseignée pour cette question.</p>}
                    </>;
                    if (projectionCorrection) {
                      const open = Boolean(projectionOpen[block.id]);
                      return <>
                        <button
                          type="button"
                          className="projection-correction-toggle"
                          aria-expanded={open}
                          aria-controls={`projection-correction-${block.id}`}
                          onClick={() => setProjectionOpen((current) => ({...current, [block.id]: !current[block.id]}))}
                        >
                          {open ? "Masquer la correction" : "Voir la correction"}
                        </button>
                        {open && <section id={`projection-correction-${block.id}`} className="student-correction-card projection-correction-card">
                          <header>Correction</header>
                          <div>{body}</div>
                        </section>}
                      </>;
                    }
                    if (!teacher) return <section className="student-correction-card">
                      <header>Correction</header>
                      <div>{body}</div>
                    </section>;
                    return <details open>
                      <summary>Correction{override ? " personnalisée" : ""}</summary>
                      {body}
                      {attempt && ["in_progress","submitted","reviewed"].includes(attempt.status) && <TeacherCorrectionEditor
                        baseText={baseCorrection}
                        overrideText={override}
                        busy={busy || openingHelp || photoBusy || autoSaving}
                        onSave={async text => {
                          if (!execute) throw new Error("Enregistrement indisponible.");
                          await execute({type:"set_correction_override",attemptId:attempt.id,expectedRevision:attempt.revision,blockId:block.id,text});
                        }}
                      />}
                    </details>;
                  })()}
                {(teacher || finalized || projectionCorrection) && block.type !== "question" && (referenceStudentCorrections[block.id] || block.studentCorrection || (block.type === "graph_task" && block.correctionGraphConfig)) && (() => {
                  const correctionText = referenceStudentCorrections[block.id] ?? block.studentCorrection;
                  const correctionBody = <><div>{correctionText && <PedagogicalCorrection text={correctionText}/>}</div>{block.type === "graph_task" && block.correctionGraphConfig && <StaticGraph config={block.correctionGraphConfig} audience="correction" constructionValues={block.correctionGraphConfig.constructions?.showValues}/>}</>;
                  if (!projectionCorrection) return <section className="student-correction-card"><header>Correction</header>{correctionBody}</section>;
                  const open = Boolean(projectionOpen[block.id]);
                  return <>
                    <button type="button" className="projection-correction-toggle" aria-expanded={open} aria-controls={`projection-correction-${block.id}`}
                      onClick={() => setProjectionOpen((current) => ({...current, [block.id]: !current[block.id]}))}>
                      {open ? "Masquer la correction" : "Voir la correction"}
                    </button>
                    {open && <section id={`projection-correction-${block.id}`} className="student-correction-card projection-correction-card"><header>Correction</header>{correctionBody}</section>}
                  </>;
                })()}
                {teacher&&block.teacherGuidance&&<details><summary>Guide professeur</summary><p><MathText text={block.teacherGuidance}/></p></details>}
                {(teacher || finalized) &&
                  block.type === "question" &&
                  block.options?.some((o) => o.correct) && (
                    <p>
                      Réponses attendues :{" "}
                      {<MathText text={block.options.filter(o=>o.correct).map(o=>o.text).join(", ")}/>}
                    </p>
                  )}
              </>
            ) : (
              (block.type==='simulation'||block.type==='animation')&&block.lockAfterCompletion&&resourceForAsset(pkg!.interactive.assets.find(a=>a.id===block.assetId)!) ? <><p>{block.prompt}</p><ValidatedManipulation resource={resourceForAsset(pkg!.interactive.assets.find(a=>a.id===block.assetId)!)!} config={block.resourceConfig} value={answers[block.id]??''} disabled={!writable} onValidate={async value=>{if(!execute)throw new Error('Enregistrement indisponible.');const active=await saveAnswers()??current.current.attempt;if(!active)throw new Error('Réalisation indisponible.');const result=await execute({type:'answer',attemptId:active.id,expectedRevision:active.revision,answers:{[block.id]:value}}) as {state:{attempts:StudentAttempt[]}};const updated=result.state.attempts.find(a=>a.id===active.id)!;baseAnswers.current={...baseAnswers.current,[block.id]:value};current.current={...current.current,attempt:updated,answers:{...current.current.answers,[block.id]:value}};setAnswers(a=>({...a,[block.id]:value}));}}/></> :
              (block.type==='simulation'||block.type==='animation')&&block.measurementTargetBlockId&&resourceForAsset(pkg!.interactive.assets.find(a=>a.id===block.assetId)!) ?
              <><h3>{block.title}</h3><p>{block.prompt}</p><ExperimentFrame remoteRead={!!attempt&&block.resourceId==='spectrophotometer'&&block.resourceVersion===3} resource={resourceForAsset(pkg!.interactive.assets.find(a=>a.id===block.assetId)!)!} config={block.resourceConfig} externalTable preview={!attempt} measurements={sourceMeasurements(answers[block.measurementTargetBlockId]??'',block.id)} disabled={!!attempt&&(!writable||evidenceLocked(attempt,block.measurementTargetBlockId)||evidenceLocked(attempt,block.id))} onEvent={event=>receiveMeasurement(block,event)}/></>:
              <BlockPreview block={block} pkg={pkg!} printMode={printMode} />
            )}
          </article>
        </Fragment>
      );})}
      {content.capabilities.supportsDirectEvaluation && (
        <p>
          Observation par le professeur pendant ou après la séance. Aucune
          réponse écrite n’est demandée.
        </p>
      )}
      {writable && content.capabilities.requiresStudentSubmission && (
        <div className="library-actions">
          <button
            disabled={busy || openingHelp || photoBusy || autoSaving}
            onClick={() => void saveAnswers().catch(() => {})}
          >
            Enregistrer mes réponses
          </button>
          <button
            disabled={busy || openingHelp || photoBusy || autoSaving}
            onClick={async () => {
              setLeaving(true);
              try {
                const updated = await saveAnswers();
                if (updated)
                  await run({
                    type: "submit",
                    attemptId: updated.id,
                    expectedRevision: updated.revision,
                  });
              } catch (e) {
                setNotice(
                  e instanceof Error ? e.message : "Remise impossible.",
                );
              } finally {setLeaving(false);}
            }}
          >
            Remettre au professeur
          </button>
        </div>
      )}
      {attempt?.evidence.length ? (
        <details>
          <summary>Preuves enregistrées ({attempt.evidence.length})</summary>
          <ul>
            {attempt.evidence.map((e) => (
              <li key={e.id}>
                {e.kind === "interaction" ? "Interaction déclarée" : "Preuve"} ·{" "}
                {e.value} · {new Date(e.recordedAt).toLocaleString("fr-FR")}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
      {correctionTabMode && selectedCorrectionGroup && (() => {
        const next = correctionGroups[correctionGroups.findIndex(group => group.id === selectedCorrectionGroup.id) + 1];
        return next ? <div className="library-actions"><button type="button" disabled={busy || openingHelp || photoBusy || autoSaving} onClick={() => {
          setActiveCorrectionGroupId(next.id);
          requestAnimationFrame(() => {
            const tab = correctionTabs.current?.querySelector<HTMLElement>('[aria-selected="true"]');
            tab?.focus({preventScroll:true});
            correctionTabs.current?.scrollIntoView({behavior:'smooth', block:'start'});
          });
        }}>Onglet suivant →</button></div> : null;
      })()}
      {canReview && (
        <article>
          <label>
            Feedback
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
            />
          </label>
          <label>
            Note facultative sur 20
            <input
              type="number"
              min="0"
              max="20"
              step="0.1"
              value={grade}
              onChange={(e) => setGrade(e.target.value)}
            />
          </label>
          <div className="library-actions">
            <button
              disabled={busy || openingHelp}
              onClick={() =>
                void run({
                  type: "review",
                  attemptId: attempt.id,
                  expectedRevision: attempt.revision,
                  results: Object.entries(results).map(([checkpointId, r]) => ({
                    checkpointId,
                    ...r,
                  })),
                  feedback: feedback ? [feedback] : [],
                  grade: grade === "" ? null : Number(grade),
                })
              }
            >
              Enregistrer l’évaluation
            </button>
            <button
              disabled={busy || reviewChanged || attempt.status !== "reviewed"}
              onClick={() =>
                void run({
                  type: "complete",
                  attemptId: attempt.id,
                  expectedRevision: attempt.revision,
                })
              }
            >
              Finaliser la correction
            </button>
          </div>
          <small>
            La finalisation fige la version, les réponses et les résultats enregistrés.
            Les compétences laissées sans choix restent Non évaluées et n’empêchent pas la restitution.
          </small>
        </article>
      )}
      {finalized && (
        <article>
          <h2>Feedback</h2>
          {attempt.feedback.length ? (
            attempt.feedback.map((f, i) => <p key={i}>{f}</p>)
          ) : (
            <p>Aucun commentaire supplémentaire.</p>
          )}
        </article>
      )}
      {notice && <p role="status">{notice}</p>}
      </div>
    </section>
  );
}



